import {FormEvent, useState} from "react";
import {useNavigate, useSearchParams, Link as RouterLink} from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {MarketingShell} from "../components/marketing-shell";
import {EXCLUSIVE_FULL_BUNDLE_BONUS_BRL, MODULE_PLANS} from "../data/plans";
import firebase from "../../core/firebase";

type FormFieldErrors = {
  businessName?: string;
  cpfCnpj?: string;
  accountName?: string;
  accountEmail?: string;
  accountPassword?: string;
  terms?: string;
};

const CHECKOUT_DRAFT_KEY = "symplou_marketing_checkout_draft";
const inputSx = {
  "& .MuiInputBase-root": {color: "#f8fafc"},
  "& .MuiInputLabel-root": {color: "rgba(226,232,240,0.78)"},
  "& .MuiInputLabel-root.Mui-focused": {color: "#14b8a6"},
  "& .MuiOutlinedInput-notchedOutline": {borderColor: "rgba(20,184,166,0.3)"},
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(20,184,166,0.6)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#14b8a6",
  },
  "& .MuiFormHelperText-root": {color: "rgba(226,232,240,0.72)"},
};

const SYMPL_GRADIENT = "linear-gradient(135deg, #0f766e 0%, #0d9488 40%, #14b8a6 100%)";

const formatCpfCnpj = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return digits
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

const TermsDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper"
    PaperProps={{ sx: { bgcolor: "#0f172a", color: "#f8fafc", border: "1px solid rgba(20,184,166,0.3)", borderRadius: 3 } }}>
    <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(148,163,184,0.15)", pb: 2 }}>
      <Typography fontWeight={800} fontSize="1.1rem">Termos de Uso e Política de Privacidade — Symplou</Typography>
      <IconButton onClick={onClose} sx={{ color: "rgba(226,232,240,0.6)" }}>
        <CloseRoundedIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ py: 3 }}>
      <Stack spacing={3} sx={{ color: "rgba(226,232,240,0.85)", fontSize: "0.92rem", lineHeight: 1.75 }}>
        <Typography variant="body2" sx={{ color: "rgba(148,163,184,0.7)" }}>
          Última atualização: 30 de abril de 2026
        </Typography>

        <Box>
          <Typography fontWeight={800} sx={{ color: "#f8fafc", mb: 1 }}>1. Aceitação dos Termos</Typography>
          <Typography variant="body2">
            Ao criar uma conta na plataforma Symplou, você declara ter lido, compreendido e concordado com estes Termos de Uso e com a Política de Privacidade aqui descritos. Caso não concorde, não utilize o serviço.
          </Typography>
        </Box>

        <Box>
          <Typography fontWeight={800} sx={{ color: "#f8fafc", mb: 1 }}>2. Descrição do Serviço</Typography>
          <Typography variant="body2">
            O Symplou é uma plataforma de gestão comercial modular voltada ao varejo e atacado, disponibilizada como serviço (SaaS) mediante assinatura mensal. O acesso é vinculado ao pagamento em dia da assinatura contratada.
          </Typography>
        </Box>

        <Box>
          <Typography fontWeight={800} sx={{ color: "#f8fafc", mb: 1 }}>3. Cadastro e Responsabilidades do Usuário</Typography>
          <Typography variant="body2">
            O usuário é responsável pela veracidade das informações fornecidas no cadastro, incluindo CPF/CNPJ, nome, e-mail e demais dados. As credenciais de acesso são de responsabilidade exclusiva do usuário, que deve mantê-las em sigilo. O uso indevido da conta por terceiros é de responsabilidade do titular.
          </Typography>
        </Box>

        <Box>
          <Typography fontWeight={800} sx={{ color: "#f8fafc", mb: 1 }}>4. Cancelamento e Rescisão</Typography>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              <strong style={{ color: "#f8fafc" }}>4.1.</strong> O usuário poderá cancelar sua assinatura a qualquer momento, sem multa ou penalidade, mediante solicitação ao suporte ou pela própria plataforma, com efeito ao final do período já pago.
            </Typography>
            <Typography variant="body2">
              <strong style={{ color: "#f8fafc" }}>4.2.</strong> O Symplou reserva-se o direito de suspender ou encerrar o acesso de qualquer conta, a qualquer momento, nas seguintes situações: (i) descumprimento destes Termos; (ii) uso fraudulento ou irregular da plataforma; (iii) necessidade técnica, operacional ou comercial devidamente justificada; (iv) encerramento definitivo do serviço.
            </Typography>
            <Typography variant="body2">
              <strong style={{ color: "#f8fafc" }}>4.3.</strong> Em caso de cancelamento por iniciativa do Symplou sem descumprimento por parte do usuário, será concedido aviso prévio mínimo de <strong style={{ color: "#2dd4bf" }}>30 (trinta) dias</strong>, exceto nos casos de violação grave dos Termos, fraude ou determinação judicial.
            </Typography>
            <Typography variant="body2">
              <strong style={{ color: "#f8fafc" }}>4.4.</strong> Após o encerramento da conta, os dados do usuário serão mantidos por até 90 (noventa) dias para eventual recuperação, sendo excluídos definitivamente após esse prazo, salvo obrigação legal em contrário.
            </Typography>
          </Stack>
        </Box>

        <Box>
          <Typography fontWeight={800} sx={{ color: "#f8fafc", mb: 1 }}>5. Pagamentos</Typography>
          <Typography variant="body2">
            Os pagamentos são processados por parceiros terceiros (ex.: Stripe). O Symplou não armazena dados de cartão de crédito ou débito. O não pagamento da assinatura mensal no prazo estipulado poderá resultar na suspensão temporária ou cancelamento definitivo do acesso, a critério do Symplou.
          </Typography>
        </Box>

        <Box>
          <Typography fontWeight={800} sx={{ color: "#f8fafc", mb: 1 }}>6. Limitação de Responsabilidade</Typography>
          <Typography variant="body2">
            O Symplou não se responsabiliza por perdas, danos diretos ou indiretos, lucros cessantes ou danos morais decorrentes de: uso indevido da plataforma pelo usuário; falhas em serviços de terceiros (internet, processador de pagamentos, nuvem); eventos de força maior ou caso fortuito; ou dados inseridos incorretamente pelo próprio usuário.
          </Typography>
        </Box>

        <Divider sx={{ borderColor: "rgba(148,163,184,0.15)" }} />

        <Box>
          <Typography fontWeight={800} sx={{ color: "#f8fafc", mb: 1 }}>7. Política de Privacidade — LGPD (Lei nº 13.709/2018)</Typography>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              <strong style={{ color: "#f8fafc" }}>7.1. Dados coletados:</strong> nome completo, e-mail, CPF/CNPJ, dados de uso da plataforma, e informações comerciais inseridas pelo usuário (produtos, clientes, vendas, movimentações financeiras, entre outros).
            </Typography>
            <Typography variant="body2">
              <strong style={{ color: "#f8fafc" }}>7.2. Finalidade do tratamento:</strong> operação e melhoria contínua da plataforma; processamento de cobranças; prestação de suporte técnico; envio de comunicações relacionadas ao serviço; e cumprimento de obrigações legais e regulatórias.
            </Typography>
            <Typography variant="body2">
              <strong style={{ color: "#f8fafc" }}>7.3. Base legal:</strong> consentimento do titular (Art. 7º, I); execução de contrato ou procedimentos preliminares (Art. 7º, V); cumprimento de obrigação legal (Art. 7º, II).
            </Typography>
            <Typography variant="body2">
              <strong style={{ color: "#f8fafc" }}>7.4. Direitos do titular:</strong> nos termos da LGPD, o usuário tem direito a confirmar a existência de tratamento, acessar seus dados, corrigir dados incompletos ou inexatos, solicitar anonimização, bloqueio ou eliminação de dados desnecessários, portabilidade e revogação do consentimento. Solicitações devem ser feitas pelo e-mail: <strong style={{ color: "#2dd4bf" }}>suporte@symplou.com.br</strong>.
            </Typography>
            <Typography variant="body2">
              <strong style={{ color: "#f8fafc" }}>7.5. Compartilhamento de dados:</strong> os dados poderão ser compartilhados com prestadores de serviço essenciais à operação da plataforma (processadores de pagamento, provedores de infraestrutura em nuvem), sempre sob cláusulas de confidencialidade e sigilo. Não comercializamos dados pessoais a terceiros.
            </Typography>
            <Typography variant="body2">
              <strong style={{ color: "#f8fafc" }}>7.6. Segurança:</strong> adotamos medidas técnicas e organizacionais adequadas para proteger os dados pessoais contra acesso não autorizado, perda, alteração ou divulgação indevida.
            </Typography>
            <Typography variant="body2">
              <strong style={{ color: "#f8fafc" }}>7.7. Encarregado de Dados (DPO):</strong> Rafael Dantas — <strong style={{ color: "#2dd4bf" }}>rafael.webdantas@gmail.com</strong>
            </Typography>
          </Stack>
        </Box>

        <Box>
          <Typography fontWeight={800} sx={{ color: "#f8fafc", mb: 1 }}>8. Propriedade Intelectual</Typography>
          <Typography variant="body2">
            Toda a plataforma Symplou, incluindo código-fonte, design, marca e funcionalidades, é de propriedade exclusiva de seus criadores e está protegida pelas leis de propriedade intelectual vigentes no Brasil. É vedada a reprodução, cópia ou engenharia reversa sem autorização expressa.
          </Typography>
        </Box>

        <Box>
          <Typography fontWeight={800} sx={{ color: "#f8fafc", mb: 1 }}>9. Alterações nos Termos</Typography>
          <Typography variant="body2">
            O Symplou poderá atualizar estes Termos a qualquer momento. Alterações relevantes serão comunicadas por e-mail cadastrado ou por aviso na própria plataforma, com antecedência mínima de 15 (quinze) dias. O uso continuado do serviço após a comunicação implica aceitação das novas condições.
          </Typography>
        </Box>

        <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(13,148,136,0.08)", border: "1px solid rgba(20,184,166,0.2)" }}>
          <Typography fontWeight={800} sx={{ color: "#f8fafc", mb: 1 }}>10. Foro Competente</Typography>
          <Typography variant="body2">
            Para dirimir quaisquer controvérsias oriundas destes Termos ou do uso da plataforma Symplou, fica eleito o foro da <strong style={{ color: "#2dd4bf" }}>Comarca de Maringá, Estado do Paraná</strong>, com renúncia expressa a qualquer outro, por mais privilegiado que seja.
          </Typography>
        </Box>
      </Stack>
    </DialogContent>
  </Dialog>
);

export const MarketingSubscribePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [cpfCnpj, setCpfCnpj] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountEmail, setAccountEmail] = useState("");
  const [accountPassword, setAccountPassword] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FormFieldErrors>({});

  const selectedModules = (searchParams.get("modules") || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  const discount = searchParams.get("discount");
  const total = searchParams.get("total");
  const bonus = searchParams.get("bonus");
  const selectedModuleNames = selectedModules.map(
    (moduleId) => MODULE_PLANS.find((m) => m.id === moduleId)?.name || moduleId
  );
  const discountPct = Number(discount || 0);
  const totalMonthly = Number(total || 0);
  const bonusApplied = bonus === "1";
  const visibleModules = selectedModuleNames.slice(0, 8);
  const hiddenModulesCount = Math.max(0, selectedModuleNames.length - visibleModules.length);

  const continueToCheckout = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});

    const cleanBusinessName = businessName.trim();
    const cleanName = accountName.trim();
    const cleanEmail = accountEmail.trim();
    const rawCpfCnpj = cpfCnpj.replace(/\D/g, "");
    const nextFieldErrors: FormFieldErrors = {};

    if (cleanBusinessName.length < 3) {
      nextFieldErrors.businessName = "O nome da loja precisa ter pelo menos 3 caracteres.";
    }
    if (rawCpfCnpj.length !== 11 && rawCpfCnpj.length !== 14) {
      nextFieldErrors.cpfCnpj = "Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido.";
    }
    if (cleanName.length < 2) {
      nextFieldErrors.accountName = "Informe o nome da conta (mínimo 2 caracteres).";
    }
    if (!cleanEmail.includes("@")) {
      nextFieldErrors.accountEmail = "Informe um email válido.";
    }
    if (accountPassword.length < 6) {
      nextFieldErrors.accountPassword = "A senha precisa ter no mínimo 6 caracteres.";
    }
    if (!termsAccepted) {
      nextFieldErrors.terms = "Você precisa aceitar os Termos de Uso e Política de Privacidade para continuar.";
    }
    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      setError("Revise os campos destacados para continuar.");
      return;
    }

    setLoading(true);
    try {
      const { httpsCallable } = await import("firebase/functions");
      const checkoutCreateAccount = httpsCallable(firebase.functions, "checkoutCreateAccount");

      const res = await checkoutCreateAccount({
        email: cleanEmail,
        name: cleanName,
        password: accountPassword,
        businessName: cleanBusinessName,
        cpfCnpj: rawCpfCnpj,
        modules: selectedModules,
        discountPct,
        totalMonthly
      });

      if ((res.data as any)?.code !== 200) {
        if ((res.data as any)?.code === 409) {
           throw { code: "auth/email-already-in-use" };
        }
        throw new Error((res.data as any)?.error || "Erro na criação");
      }

      const { signInWithEmailAndPassword } = await import("firebase/auth");
      await signInWithEmailAndPassword(firebase.auth, cleanEmail, accountPassword);

      navigate("/checkout", { state: {
        businessName: cleanBusinessName,
        modules: selectedModules,
        discountPct,
        totalMonthly,
        bonusApplied,
        accountName: cleanName,
        accountEmail: cleanEmail,
      } });
    } catch (err) {
      const code = (err as {code?: string}).code || "";
      if (code === "auth/email-already-in-use") {
        setError("Este email já está em uso. Faça login para continuar.");
      } else {
        setError("Não foi possível criar a conta agora. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <MarketingShell>
      <TermsDialog open={termsOpen} onClose={() => setTermsOpen(false)} />

      <Box sx={{maxWidth: 1000, mx: "auto", px: { xs: 2, md: 0 }, pb: 5}}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                bgcolor: "rgba(15,23,42,0.8)",
                border: "1px solid rgba(20,184,166,0.3)",
                boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
                borderRadius: 4,
                overflow: "hidden"
              }}
            >
              <Box sx={{
                background: SYMPL_GRADIENT,
                py: 2.5, px: 3,
                borderBottom: "1px solid rgba(255,255,255,0.1)",
              }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      bgcolor: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: 24, color: "#fff" }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{ opacity: 0.95, fontWeight: 800, letterSpacing: "0.15em", lineHeight: 1.2, color: "#fff" }}
                    >
                      Symplou Premium
                    </Typography>
                    <Typography variant="h6" fontWeight={900} sx={{ letterSpacing: "-0.02em", lineHeight: 1.2, color: "#fff" }}>
                      Resumo da contratação
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <CardContent sx={{p: {xs: 3, md: 4}}}>
                <Stack spacing={3}>
                  <Box sx={{ p: 2.5, borderRadius: 3, bgcolor: "rgba(2,6,23,0.5)", border: "1px solid rgba(148,163,184,0.15)" }}>
                    <Typography sx={{ color: "rgba(226,232,240,0.7)", fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5, fontWeight: 700 }}>
                      Plano
                    </Typography>
                    <Typography sx={{ fontWeight: 800, color: "#f8fafc", fontSize: "1.1rem" }}>
                      Pacote Modular Personalizado
                    </Typography>
                  </Box>

                  <Box>
                    <Typography sx={{ fontWeight: 800, mb: 1.5, color: "#f8fafc" }}>
                      Módulos Integrados ({selectedModules.length})
                    </Typography>
                    <Stack spacing={1}>
                      {visibleModules.map((name) => (
                        <Stack key={name} direction="row" alignItems="center" spacing={1.5}>
                          <CheckCircleOutlineIcon sx={{ color: "#14b8a6", fontSize: 18 }} />
                          <Typography sx={{ color: "rgba(226,232,240,0.9)", fontSize: "0.95rem" }}>
                            {name}
                          </Typography>
                        </Stack>
                      ))}
                      {hiddenModulesCount > 0 ? (
                        <Typography sx={{ color: "#2dd4bf", fontSize: "0.85rem", fontWeight: 700, mt: 1 }}>
                          + {hiddenModulesCount} módulo(s) avançado(s)
                        </Typography>
                      ) : null}
                    </Stack>
                  </Box>

                  <Divider sx={{ borderColor: "rgba(148,163,184,0.1)" }} />

                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography sx={{ color: "rgba(226,232,240,0.7)", fontSize: "1rem" }}>Desconto progressivo</Typography>
                      <Chip label={`- ${discountPct}%`} size="small" sx={{ bgcolor: "rgba(20,184,166,0.15)", color: "#2dd4bf", fontWeight: 800, borderRadius: 1 }} />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
                      <Typography sx={{ color: "#f8fafc", fontSize: "1.1rem", fontWeight: 700 }}>Total a faturar</Typography>
                      <Typography sx={{ fontSize: "2rem", fontWeight: 900, color: "#14b8a6", lineHeight: 1 }}>
                        R$ {totalMonthly.toFixed(2).replace(".", ",")}
                      </Typography>
                    </Stack>
                  </Stack>

                  {bonus === "1" ? (
                    <Alert severity="success" sx={{ bgcolor: "rgba(13,148,136,0.1)", border: "1px solid rgba(20,184,166,0.3)", color: "#2dd4bf", "& .MuiAlert-icon": { color: "#2dd4bf" } }}>
                      Bônus de implantação aplicado: <strong>R$ {EXCLUSIVE_FULL_BUNDLE_BONUS_BRL},00</strong> em crédito de Inteligência Artificial liberados.
                    </Alert>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                bgcolor: "rgba(15,23,42,0.8)",
                border: "1px solid rgba(148,163,184,0.15)",
                boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
                borderRadius: 4
              }}
            >
              <CardContent sx={{p: {xs: 3, md: 4}}}>
                <Stack spacing={3}>
                  <Box>
                    <Typography sx={{ textTransform: "uppercase", letterSpacing: "0.15em", color: "#14b8a6", fontWeight: 800, fontSize: "0.75rem", mb: 0.5 }}>
                      Etapa final
                    </Typography>
                    <Typography variant="h5" fontWeight={900} sx={{ color: "#f8fafc", mb: 1 }}>
                      Criar conta administrativa
                    </Typography>
                    <Typography sx={{color: "rgba(226,232,240,0.7)"}}>
                      Preencha os dados do responsável para liberar o checkout seguro e ativar sua assinatura.
                    </Typography>
                  </Box>

                  <Box component="form" onSubmit={continueToCheckout} noValidate>
                    <Stack spacing={2}>
                      <TextField
                        label="Nome da sua loja"
                        placeholder="Minha Loja Fantástica"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        fullWidth
                        sx={inputSx}
                        error={Boolean(fieldErrors.businessName)}
                        helperText={fieldErrors.businessName}
                      />
                      <TextField
                        label="CPF ou CNPJ da loja"
                        placeholder="000.000.000-00 ou 00.000.000/0000-00"
                        value={cpfCnpj}
                        onChange={(e) => setCpfCnpj(formatCpfCnpj(e.target.value))}
                        fullWidth
                        inputProps={{ inputMode: "numeric" }}
                        sx={inputSx}
                        error={Boolean(fieldErrors.cpfCnpj)}
                        helperText={fieldErrors.cpfCnpj || "Pessoa física (CPF) ou jurídica (CNPJ)"}
                      />
                      <TextField
                        label="Nome do titular"
                        value={accountName}
                        onChange={(e) => setAccountName(e.target.value)}
                        fullWidth
                        sx={inputSx}
                        error={Boolean(fieldErrors.accountName)}
                        helperText={fieldErrors.accountName}
                      />
                      <TextField
                        label="Email profissional"
                        type="email"
                        value={accountEmail}
                        onChange={(e) => setAccountEmail(e.target.value)}
                        fullWidth
                        sx={inputSx}
                        error={Boolean(fieldErrors.accountEmail)}
                        helperText={fieldErrors.accountEmail}
                      />
                      <TextField
                        label="Senha segura"
                        type="password"
                        value={accountPassword}
                        onChange={(e) => setAccountPassword(e.target.value)}
                        fullWidth
                        helperText={fieldErrors.accountPassword || "Mínimo de 6 caracteres"}
                        sx={inputSx}
                        error={Boolean(fieldErrors.accountPassword)}
                      />

                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: fieldErrors.terms
                            ? "1px solid rgba(239,68,68,0.6)"
                            : "1px solid rgba(148,163,184,0.15)",
                          bgcolor: "rgba(2,6,23,0.4)",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={termsAccepted}
                              onChange={(e) => setTermsAccepted(e.target.checked)}
                              sx={{
                                color: "rgba(148,163,184,0.5)",
                                "&.Mui-checked": { color: "#14b8a6" },
                                pt: 0,
                                alignSelf: "flex-start",
                                mt: 0.3,
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ color: "rgba(226,232,240,0.8)", lineHeight: 1.6 }}>
                              Li e aceito os{" "}
                              <Box
                                component="span"
                                onClick={(e) => { e.preventDefault(); setTermsOpen(true); }}
                                sx={{ color: "#14b8a6", fontWeight: 700, cursor: "pointer", textDecoration: "underline", "&:hover": { color: "#2dd4bf" } }}
                              >
                                Termos de Uso e Política de Privacidade
                              </Box>
                              {" "}do Symplou, incluindo as disposições sobre cancelamento e proteção de dados (LGPD).
                            </Typography>
                          }
                          sx={{ m: 0, alignItems: "flex-start" }}
                        />
                        {fieldErrors.terms ? (
                          <Typography variant="caption" sx={{ color: "rgba(239,68,68,0.9)", mt: 0.5, display: "block", pl: 4 }}>
                            {fieldErrors.terms}
                          </Typography>
                        ) : null}
                      </Box>

                      {error ? <Alert severity="error">{error}</Alert> : null}

                      <Box sx={{ pt: 1 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          fullWidth
                          size="large"
                          disabled={loading}
                          endIcon={!loading && <ArrowForwardRoundedIcon />}
                          sx={{
                            background: SYMPL_GRADIENT,
                            fontWeight: 800,
                            py: 1.5,
                            fontSize: "1.05rem"
                          }}
                        >
                          {loading ? "Criando conta..." : "Ir para o Pagamento Seguro"}
                        </Button>
                        <Stack alignItems="center" sx={{ mt: 2 }}>
                          <Button component={RouterLink} to="/planos" variant="text" sx={{ color: "rgba(226,232,240,0.5)", fontWeight: 600, "&:hover": { color: "#f8fafc", bgcolor: "transparent" } }}>
                            Voltar e alterar módulos
                          </Button>
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </MarketingShell>
  );
};
